// views/proveedores/productos/Productos.js
import React, { Fragment, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import {
    buscarProductosProveedor,
    obtenerProveedor,
    limpiarProveedor
} from '../../../actions/proveedores';
import { formatNumber } from '../../../utils/generalFunctions';

import NuevoProductoProveedor from './NuevoProductoProveedor';
import EditarProductoProveedor from './EditarProductoProveedor';
import EliminarProductoProveedor from './EliminarProductoProveedor';
import AjustarPreciosProveedorModal from './AjustarPreciosProveedorModal';

const ProductosProveedores = () => {

    const dispatch = useDispatch();
    const { id: id_proveedor } = useParams();

    const { productos, detalles: proveedorDetalles } = useSelector(state => state.proveedores);
    const { listado } = productos || { listado: [] }; // arreglo de productos de ese proveedor

    const [productoEditar, setProductoEditar] = useState(null);
    const [productoEliminar, setProductoEliminar] = useState(null);
    const [modalNuevoProducto, setModalNuevoProducto] = useState(false);
    const [modalAjustarPrecios, setModalAjustarPrecios] = useState(false);

    const nombreProveedor = proveedorDetalles?.nombre || 'Proveedor';

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    const formatPrecio = (valor) => {
        if (valor === null || valor === undefined || valor === '') {
            return '$ 0.00';
        }
        const numero = Number(valor);
        if (Number.isNaN(numero)) {
            return '$ 0.00';
        }
        return formatNumber(numero, 2, '$ ', '');
    };

    const handleEditar = (row) => {
        setProductoEditar(row);
    };

    const handleEliminar = (row) => {
        setProductoEliminar(row);
    };

    const columnas = [
        {
            name: 'Código tienda',
            selector: row => row.codigo_tienda,
            sortable: true,
            wrap: true
        },
        {
            name: 'Nombre producto',
            selector: row => row.nombre,
            sortable: true,
            wrap: true
        },
        {
            name: 'Código proveedor',
            selector: row => row.codigo_proveedor,
            sortable: true,
            wrap: true
        },
        {
            name: 'Precio público',
            selector: row => row.precio_publico,
            sortable: true,
            right: true,
            cell: row => formatPrecio(row.precio_publico)
        },
        {
            name: 'Precio mínimo',
            selector: row => row.precio_minimo,
            sortable: true,
            right: true,
            cell: row => formatPrecio(row.precio_minimo)
        },
        {
            name: 'Precio liquidación',
            selector: row => row.precio_liquidacion,
            sortable: true,
            right: true,
            cell: row => formatPrecio(row.precio_liquidacion)
        },
        {
            name: 'Acciones',
            cell: (row) => (
                <div className="d-flex">
                    <button
                        className="btn btn-sm btn-link text-primary px-1"
                        onClick={() => handleEditar(row)}
                    >
                        <i className="fas fa-edit"></i>
                    </button>
                    <button
                        className="btn btn-sm btn-link text-danger px-1"
                        onClick={() => handleEliminar(row)}
                    >
                        <i className="fas fa-trash"></i>
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true
        }
    ];

    // Carga productos del proveedor
    useEffect(() => {
        if (!id_proveedor) return;
        dispatch(buscarProductosProveedor(id_proveedor));
    }, [dispatch, id_proveedor]);

    // Carga datos del proveedor (para el nombre) y limpia al salir
    useEffect(() => {
        if (!id_proveedor) return;

        dispatch(limpiarProveedor());
        dispatch(obtenerProveedor(id_proveedor));

        return () => {
            dispatch(limpiarProveedor());
        };
    }, [dispatch, id_proveedor]);

    // Breadcrumb usando el nombre del proveedor
    useEffect(() => {
        if (!id_proveedor) return;

        const rutasBread = [
            {
                activo: false,
                nombre: 'Proveedores',
                path: '/proveedores'
            },
            {
                activo: true,
                nombre: `Productos de ${nombreProveedor}`,
                path: `/proveedores/productos/${id_proveedor}`
            }
        ];
        dispatch(editarBreadcrumb(rutasBread));
    }, [dispatch, id_proveedor, nombreProveedor]);

    return (
        <Fragment>
            <div className="row mb-3">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">
                        Productos de {nombreProveedor}
                    </h3>

                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => setModalAjustarPrecios(true)}
                        >
                            <i className="fas fa-percent fa-sm"></i> Ajustar precios
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setModalNuevoProducto(true)}
                        >
                            Agregar producto
                        </button>
                    </div>

                    <div className="mt-2">
                        <DataTable
                            columns={columnas}
                            data={listado || []}
                            pagination
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover
                            dense
                            noDataComponent="No hay productos registrados para este proveedor"
                        />
                    </div>
                </div>
            </div>

            <NuevoProductoProveedor
                show={modalNuevoProducto}
                idProveedor={id_proveedor}
                onClose={() => setModalNuevoProducto(false)}
            />

            <EditarProductoProveedor
                show={!!productoEditar}
                onClose={() => setProductoEditar(null)}
                producto={productoEditar}
                idProveedor={id_proveedor}
            />

            <EliminarProductoProveedor
                producto={productoEliminar}
                reestablecerProducto={setProductoEliminar}
            />

            <AjustarPreciosProveedorModal
                show={modalAjustarPrecios}
                onClose={() => setModalAjustarPrecios(false)}
                idProveedor={id_proveedor}
                nombreProveedor={nombreProveedor}
            />
        </Fragment>
    );
};

export default ProductosProveedores;
